import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import AdminMenuArrange from "../components/AdminMenuArrange";
import { api, formatError } from "../lib/api";
import { broadcastMenuVisibilityChanged } from "../lib/menuVisibilityBus";
import { toast } from "sonner";
import {
  Rocket, Eye, EyeOff, Save, RotateCcw, Plus, Trash2,
  GripVertical, Pencil, X, Check, Lock, RefreshCcw, AlertTriangle,
  Users2, ChevronDown, ShieldCheck, FileCog,
  Smartphone, Tablet, Monitor, Info,
} from "lucide-react";

// A single Menu Directory doc drives the whole sidebar. We keep everything
// in local state, let the admin drag / rename / add / delete freely, and
// only PUT the final layout when they hit Save.
export default function AdminMenuDirectory() {
  const [scope, setScope] = useState("sidebar"); // "sidebar" | "admin" | "pages"
  const [sections, setSections] = useState([]);   // [{id, label, order, deletable, custom, enabled, plans}]
  const [items, setItems] = useState([]);         // [{id, section, label, order, kind, protected, enabled, plans}]
  const [plansCatalog, setPlansCatalog] = useState([]); // [{slug, label}]
  // Children of dropdown items (Course Modules, Financial Tools, Databases).
  // `childrenSource[parentId]` is the authoritative list from Mongo, and
  // `childrenOverrides[parentId][childId]` = {label, order, plans, disabled}.
  const [childrenSource, setChildrenSource] = useState({});
  const [childrenOverrides, setChildrenOverrides] = useState({});
  const [expandedDropdowns, setExpandedDropdowns] = useState(new Set());
  const [protectedItem, setProtectedItem] = useState("nav-account");
  // Pages tab — flat list of page-element toggles (e.g. hide the
  // "Add to LinkedIn" button on /progress). `pages` is the static
  // registry shipped by the backend; `pageElementsDisabled` is the
  // set of ids the admin has flipped off.
  const [pages, setPages] = useState([]);
  const [pageElementsDisabled, setPageElementsDisabled] = useState(new Set());
  const [initial, setInitial] = useState({
    sections: [], items: [], children_overrides: {},
    page_elements_disabled: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Drag state — { type: "item"|"section", id, from? }
  const drag = useRef(null);
  const [dropHint, setDropHint] = useState(null); // {sectionId?, itemId?, position: "before"|"after"|"inside"}

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null); // section obj or null

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/menu-directory");
      const secs = (data.sections || []).slice().sort((a, b) => a.order - b.order)
        .map((s) => ({ ...s, plans: s.plans || [] }));
      const its = (data.items || []).slice().sort((a, b) =>
        a.section === b.section ? a.order - b.order : 0)
        .map((it) => ({ ...it, plans: it.plans || [] }));
      setSections(secs);
      setItems(its);
      setPlansCatalog(data.plans || []);
      setChildrenSource(data.children_source || {});
      setChildrenOverrides(data.children_overrides || {});
      setProtectedItem(data.protected_item || "nav-account");
      const pageRegistry = data.pages || [];
      const pageDisabled = new Set(data.page_elements_disabled || []);
      setPages(pageRegistry);
      setPageElementsDisabled(pageDisabled);
      setInitial({
        sections: secs, items: its,
        children_overrides: data.children_overrides || {},
        page_elements_disabled: Array.from(pageDisabled).sort(),
      });
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const dirty = useMemo(() => {
    const nowPageDisabled = Array.from(pageElementsDisabled).sort();
    return (
      JSON.stringify(serialize(sections, items, childrenOverrides)) !==
        JSON.stringify(serialize(initial.sections, initial.items, initial.children_overrides || {})) ||
      JSON.stringify(nowPageDisabled) !==
        JSON.stringify(initial.page_elements_disabled || [])
    );
  }, [sections, items, childrenOverrides, pageElementsDisabled, initial]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sections: sections.map((s, idx) => ({
          id: s.id, label: s.label, order: idx, plans: s.plans || [],
        })),
        items: items.map((it) => ({
          id: it.id, section: it.section, label: it.label,
          order: it.order, plans: it.plans || [],
          viewports: it.viewports || { mobile: true, tablet: true, desktop: true },
        })),
        disabled: [
          ...sections.filter((s) => !s.enabled).map((s) => s.id),
          ...items.filter((it) => !it.enabled).map((it) => it.id),
        ],
        coming_soon: items.filter((it) => it.coming_soon && !it.protected).map((it) => it.id),
        is_new: items.filter((it) => it.is_new).map((it) => it.id),
        children_overrides: childrenOverrides,
        page_elements_disabled: Array.from(pageElementsDisabled).sort(),
      };
      await api.put("/admin/menu-directory", payload);
      // Notify every open tab (including this one) that the layout
      // just changed so pages reading /menu-visibility can re-fetch
      // WITHOUT a page reload. Same-tab CustomEvent + cross-tab
      // BroadcastChannel are dispatched from the bus helper.
      broadcastMenuVisibilityChanged();
      toast.success("Menu saved — every open tab will refresh automatically.");
      setInitial({
        sections, items, children_overrides: childrenOverrides,
        page_elements_disabled: Array.from(pageElementsDisabled).sort(),
      });
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  const revert = () => {
    setSections(initial.sections);
    setItems(initial.items);
    setChildrenOverrides(initial.children_overrides || {});
    setPageElementsDisabled(new Set(initial.page_elements_disabled || []));
    setRenamingId(null);
  };

  // Pages tab — flip a single page-element toggle. Kept as a set-based
  // helper so callers don't have to think about immutable Set copies.
  const togglePageElement = (elementId) => {
    setPageElementsDisabled((prev) => {
      const next = new Set(prev);
      if (next.has(elementId)) next.delete(elementId);
      else next.add(elementId);
      return next;
    });
  };

  const resetToBaseline = async () => {
    if (!window.confirm("Reset menu to the default arrangement? This drops any custom sections.")) return;
    try {
      await api.post("/admin/menu-directory/reset");
      toast.success("Menu reset to defaults.");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  // ---- Toggle enabled ----
  const toggleSectionEnabled = (sid) => {
    setSections((prev) => prev.map((s) => (s.id === sid ? { ...s, enabled: !s.enabled } : s)));
  };
  const toggleItemEnabled = (iid) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== iid) return it;
      if (it.protected) return it; // never toggle protected
      return { ...it, enabled: !it.enabled };
    }));
  };
  // Coming Soon + New overlays. These are per-item and independent of
  // `enabled` — an item can be visible AND marked coming-soon (frozen
  // with a chip) or visible AND marked new (chip only). Protected items
  // (nav-account) can never be marked coming-soon since that would trap
  // members. `is_new` on protected item is harmless and allowed.
  // The two overlays are mutually exclusive: flipping one ON auto-clears
  // the other so a row never carries "COMING SOON" and "NEW" together.
  const toggleItemComingSoon = (iid) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== iid) return it;
      if (it.protected) return it;
      const next = !it.coming_soon;
      return { ...it, coming_soon: next, is_new: next ? false : it.is_new };
    }));
  };
  const toggleItemNew = (iid) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== iid) return it;
      const next = !it.is_new;
      return { ...it, is_new: next, coming_soon: next ? false : it.coming_soon };
    }));
  };

  // ---- Per-viewport visibility ----
  // Each item carries a { mobile, tablet, desktop } bool trio. Toggling
  // one flips its clickable state on that viewport; the item still
  // renders in the sidebar there, but shows a "View on Desktop" chip
  // and stops navigating. Direct URL access is blocked by
  // <ViewportRouteGuard> in AppShell so a shared link can't sneak the
  // page onto a hidden viewport. Protected item (nav-account) is left
  // alone so no admin can trap members on a small screen.
  const toggleItemViewport = (iid, vp) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== iid) return it;
      if (it.protected) return it;
      const current = it.viewports || { mobile: true, tablet: true, desktop: true };
      return { ...it, viewports: { ...current, [vp]: !current[vp] } };
    }));
  };

  // ---- Rename ----
  // `renamingId` can be either a section/item id, or "child:<parentId>:<childId>".
  const startRename = (id, current) => { setRenamingId(id); setRenameValue(current); };
  const commitRename = () => {
    const val = renameValue.trim();
    if (!val) { setRenamingId(null); return; }
    if (renamingId && renamingId.startsWith("child:")) {
      const [, parentId, childId] = renamingId.split(":");
      renameChild(parentId, childId, val);
    } else {
      setSections((prev) => prev.map((s) => (s.id === renamingId ? { ...s, label: val } : s)));
      setItems((prev)    => prev.map((it) => (it.id === renamingId ? { ...it, label: val } : it)));
    }
    setRenamingId(null);
  };
  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };

  // ---- Add section ----
  const addSection = () => {
    const label = window.prompt("Name your new section:", "New section");
    if (!label || !label.trim()) return;
    const id = `section-custom-${randHex(12)}`;
    setSections((prev) => [
      ...prev,
      { id, label: label.trim(), order: prev.length, deletable: true, custom: true, enabled: true, plans: [] },
    ]);
  };

  // ---- Plans ----
  const setSectionPlans = (sid, plans) => {
    setSections((prev) => prev.map((s) => (s.id === sid ? { ...s, plans } : s)));
  };
  const setItemPlans = (iid, plans) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== iid) return it;
      if (it.protected) return it; // nav-account must stay on all plans
      return { ...it, plans };
    }));
  };

  // ---- Dropdown children (Course Modules / Financial Tools / Databases) ----
  const toggleDropdown = (parentId) => {
    setExpandedDropdowns((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId); else next.add(parentId);
      return next;
    });
  };
  const patchChild = (parentId, childId, patch) => {
    setChildrenOverrides((prev) => {
      const next = { ...prev };
      const parent = { ...(next[parentId] || {}) };
      const before = parent[childId] || {};
      const merged = { ...before, ...patch };
      // Prune keys equal to source defaults (empty plans + no override).
      const isEmpty =
        (merged.label === undefined || merged.label === "") &&
        merged.order === undefined &&
        merged.disabled !== true &&
        (!merged.plans || merged.plans.length === 0);
      if (isEmpty) delete parent[childId];
      else parent[childId] = merged;
      if (Object.keys(parent).length === 0) delete next[parentId];
      else next[parentId] = parent;
      return next;
    });
  };
  // Reorder a child within its parent: build an ordered list, splice, then
  // rewrite the `order` override for every entry so the parent's list is
  // stable regardless of source ordering.
  const reorderChild = (parentId, sourceChildId, targetChildId, before) => {
    const merged = mergedChildren(parentId, childrenSource, childrenOverrides);
    const src = merged.find((c) => c.id === sourceChildId);
    if (!src) return;
    const filtered = merged.filter((c) => c.id !== sourceChildId);
    const tIdx = filtered.findIndex((c) => c.id === targetChildId);
    if (tIdx < 0) return;
    filtered.splice(before ? tIdx : tIdx + 1, 0, src);
    setChildrenOverrides((prev) => {
      const next = { ...prev };
      const parent = { ...(next[parentId] || {}) };
      filtered.forEach((c, idx) => {
        parent[c.id] = { ...(parent[c.id] || {}), order: idx };
      });
      next[parentId] = parent;
      return next;
    });
  };
  const renameChild = (parentId, childId, newLabel) => {
    const source = (childrenSource[parentId] || []).find((c) => c.id === childId);
    const sourceLabel = source?.label || "";
    const trimmed = (newLabel || "").trim();
    // Store override only if it differs from the source label.
    if (!trimmed || trimmed === sourceLabel) {
      patchChild(parentId, childId, { label: undefined });
    } else {
      patchChild(parentId, childId, { label: trimmed });
    }
  };

  // ---- Delete section (with typed-DELETE modal) ----
  const requestDelete = (section) => {
    if (!section.deletable) {
      toast.error("The Settings section can't be deleted — it anchors your Account & Access page.");
      return;
    }
    setDeleteTarget(section);
  };
  const performDelete = (sectionId, moveToSectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    setItems((prev) => prev.map((it) => (
      it.section === sectionId ? { ...it, section: moveToSectionId } : it
    )));
    setDeleteTarget(null);
  };

  // ---- Drag & drop ----
  const onItemDragStart = (e, iid) => {
    drag.current = { type: "item", id: iid };
    e.dataTransfer.setData("text/plain", iid);
    e.dataTransfer.effectAllowed = "move";
  };
  const onSectionDragStart = (e, sid) => {
    drag.current = { type: "section", id: sid };
    e.dataTransfer.setData("text/plain", sid);
    e.dataTransfer.effectAllowed = "move";
  };
  const clearDrag = () => { drag.current = null; setDropHint(null); };

  const onItemDragOver = (e, targetId) => {
    if (!drag.current || drag.current.type !== "item") return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - rect.top) < rect.height / 2;
    setDropHint({ itemId: targetId, position: before ? "before" : "after" });
  };
  const onItemDrop = (e, targetItemId) => {
    if (!drag.current || drag.current.type !== "item") return;
    e.preventDefault();
    e.stopPropagation();
    const sourceId = drag.current.id;
    if (sourceId === targetItemId) { clearDrag(); return; }
    const targetIt = items.find((x) => x.id === targetItemId);
    if (!targetIt) { clearDrag(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - rect.top) < rect.height / 2;
    moveItemNear(sourceId, targetIt.section, targetItemId, before ? "before" : "after");
    clearDrag();
  };

  const onSectionDropZone = (e, sid) => {
    if (!drag.current) return;
    e.preventDefault();
    e.stopPropagation();
    if (drag.current.type === "item") {
      moveItemToEndOfSection(drag.current.id, sid);
    }
    clearDrag();
  };
  const onSectionDragOverZone = (e) => {
    if (!drag.current || drag.current.type !== "item") return;
    e.preventDefault();
  };

  const onSectionHeaderDragOver = (e, sid) => {
    if (!drag.current || drag.current.type !== "section") return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - rect.top) < rect.height / 2;
    setDropHint({ sectionId: sid, position: before ? "before" : "after" });
  };
  const onSectionHeaderDrop = (e, targetSid) => {
    if (!drag.current || drag.current.type !== "section") return;
    e.preventDefault();
    e.stopPropagation();
    const sourceSid = drag.current.id;
    if (sourceSid === targetSid) { clearDrag(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - rect.top) < rect.height / 2;
    reorderSectionNear(sourceSid, targetSid, before);
    clearDrag();
  };

  const moveItemNear = (sourceId, targetSection, targetItemId, position) => {
    setItems((prev) => {
      const src = prev.find((x) => x.id === sourceId);
      if (!src) return prev;
      const withoutSrc = prev.filter((x) => x.id !== sourceId);
      const insertedSrc = { ...src, section: targetSection };
      const secItems = withoutSrc
        .filter((x) => x.section === targetSection)
        .sort((a, b) => a.order - b.order);
      const tIdx = secItems.findIndex((x) => x.id === targetItemId);
      if (tIdx < 0) {
        secItems.push(insertedSrc);
      } else {
        secItems.splice(position === "before" ? tIdx : tIdx + 1, 0, insertedSrc);
      }
      // rewrite order for the affected section
      secItems.forEach((x, idx) => { x.order = idx; });
      const others = withoutSrc.filter((x) => x.section !== targetSection);
      return [...others, ...secItems];
    });
  };

  const moveItemToEndOfSection = (sourceId, targetSection) => {
    setItems((prev) => {
      const src = prev.find((x) => x.id === sourceId);
      if (!src) return prev;
      const withoutSrc = prev.filter((x) => x.id !== sourceId);
      const secItems = withoutSrc
        .filter((x) => x.section === targetSection)
        .sort((a, b) => a.order - b.order);
      secItems.push({ ...src, section: targetSection });
      secItems.forEach((x, idx) => { x.order = idx; });
      const others = withoutSrc.filter((x) => x.section !== targetSection);
      return [...others, ...secItems];
    });
  };

  const reorderSectionNear = (sourceSid, targetSid, before) => {
    setSections((prev) => {
      const src = prev.find((s) => s.id === sourceSid);
      if (!src) return prev;
      const filtered = prev.filter((s) => s.id !== sourceSid);
      const tIdx = filtered.findIndex((s) => s.id === targetSid);
      if (tIdx < 0) return prev;
      filtered.splice(before ? tIdx : tIdx + 1, 0, src);
      return filtered.map((s, idx) => ({ ...s, order: idx }));
    });
  };

  // ------------------ Render ------------------
  return (
    <AppShell title="Menu Directory">
      <div className="w-full">
        <PageHero
          eyebrow="Sidebar layout"
          icon={Rocket}
          title={<>Menu <span className="text-grad">Directory</span></>}
          subtitle="Rearrange the sidebar to fit how your team works. Drag items between sections, rename anything, add new sections, or hide entries entirely. Changes apply everywhere on the next page load."
          testid="menu-directory"
          right={
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={resetToBaseline}
                data-testid="menu-directory-reset"
                className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted"
                title="Restore default sections + labels"
              >
                <RefreshCcw className="w-3.5 h-3.5" /> Reset to defaults
              </button>
              {dirty ? (
                <>
                  <button onClick={revert} disabled={saving} data-testid="menu-directory-revert"
                    className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Revert
                  </button>
                  <button onClick={save} disabled={saving} data-testid="menu-directory-save"
                    className="btn-amber inline-flex items-center gap-1 text-xs disabled:opacity-60">
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  <Eye className="w-3 h-3" /> Live
                </span>
              )}
            </div>
          }
        />

        {/* Scope switcher — pick which menu to arrange */}
        <div className="inline-flex rounded-full border border-border p-1 bg-white mb-6"
             data-testid="menu-directory-scope">
          <button
            onClick={() => setScope("sidebar")}
            data-testid="menu-directory-scope-sidebar"
            className={[
              "px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest transition inline-flex items-center gap-1.5",
              scope === "sidebar" ? "bg-primary text-white shadow-sm"
                                  : "text-foreground/70 hover:text-foreground",
            ].join(" ")}
          >
            <Rocket className="w-3.5 h-3.5" /> Member sidebar
          </button>
          <button
            onClick={() => setScope("admin")}
            data-testid="menu-directory-scope-admin"
            className={[
              "px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest transition inline-flex items-center gap-1.5",
              scope === "admin" ? "bg-primary text-white shadow-sm"
                                : "text-foreground/70 hover:text-foreground",
            ].join(" ")}
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Admin menu
          </button>
          <button
            onClick={() => setScope("pages")}
            data-testid="menu-directory-scope-pages"
            className={[
              "px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest transition inline-flex items-center gap-1.5",
              scope === "pages" ? "bg-primary text-white shadow-sm"
                                : "text-foreground/70 hover:text-foreground",
            ].join(" ")}
          >
            <FileCog className="w-3.5 h-3.5" /> Pages
          </button>
        </div>

        {scope === "admin" ? (
          <AdminMenuArrange />
        ) : scope === "pages" ? (
          <PagesPanel
            pages={pages}
            disabled={pageElementsDisabled}
            onToggle={togglePageElement}
          />
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading catalog…</p>
        ) : (
          <>
            <ViewportVisibilityNote />
          <div className="grid gap-3" data-testid="menu-directory-list">
            {sections.map((sec) => {
              const secItems = items
                .filter((it) => it.section === sec.id)
                .sort((a, b) => a.order - b.order);
              const secDisabled = !sec.enabled;
              const showBeforeHint = dropHint?.sectionId === sec.id && dropHint.position === "before";
              const showAfterHint = dropHint?.sectionId === sec.id && dropHint.position === "after";
              return (
                <React.Fragment key={sec.id}>
                  {showBeforeHint && <DropDivider />}
                  <div
                    className={["surface p-4 transition-opacity",
                      secDisabled ? "opacity-70" : ""].join(" ")}
                    data-testid={`menu-directory-section-${sec.id}`}
                    onDragOver={onSectionDragOverZone}
                    onDrop={(e) => onSectionDropZone(e, sec.id)}
                  >
                    <div
                      className="flex items-center gap-2 pb-3 border-b border-shell-border/60 cursor-move select-none"
                      draggable
                      onDragStart={(e) => onSectionDragStart(e, sec.id)}
                      onDragEnd={clearDrag}
                      onDragOver={(e) => onSectionHeaderDragOver(e, sec.id)}
                      onDrop={(e) => onSectionHeaderDrop(e, sec.id)}
                      data-testid={`menu-directory-section-header-${sec.id}`}
                    >
                      <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                      {renamingId === sec.id ? (
                        <RenameField
                          value={renameValue}
                          onChange={setRenameValue}
                          onCommit={commitRename}
                          onCancel={cancelRename}
                          testid={`menu-directory-rename-input-${sec.id}`}
                        />
                      ) : (
                        <button
                          onClick={() => startRename(sec.id, sec.label)}
                          className="font-display text-lg flex-1 text-left hover:text-primary transition"
                          data-testid={`menu-directory-section-label-${sec.id}`}
                        >
                          {sec.label}
                        </button>
                      )}
                      {sec.custom && (
                        <span className="text-[9.5px] font-bold uppercase tracking-widest text-primary/80 shrink-0">
                          Custom
                        </span>
                      )}
                      <button
                        onClick={() => startRename(sec.id, sec.label)}
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground"
                        title="Rename section"
                        data-testid={`menu-directory-rename-section-${sec.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <PlansSelector
                        id={sec.id}
                        selected={sec.plans || []}
                        catalog={plansCatalog}
                        onChange={(plans) => setSectionPlans(sec.id, plans)}
                      />
                      <Toggle
                        id={sec.id}
                        on={sec.enabled}
                        onToggle={() => toggleSectionEnabled(sec.id)}
                      />
                      <button
                        onClick={() => requestDelete(sec)}
                        disabled={!sec.deletable}
                        className={["p-1.5 rounded transition-colors",
                          sec.deletable ? "text-rose-500 hover:bg-rose-500/10"
                                        : "text-muted-foreground/30 cursor-not-allowed"].join(" ")}
                        title={sec.deletable ? "Delete section" : "The Settings section can't be deleted"}
                        data-testid={`menu-directory-delete-section-${sec.id}`}
                      >
                        {sec.deletable ? <Trash2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                      </button>
                    </div>

                    <div className="mt-3 grid gap-1 pl-1 border-l-2 border-shell-border/50 min-h-[24px]">
                      {secItems.length === 0 && (
                        <p className="pl-3 py-2 text-xs italic text-muted-foreground">
                          Drop items here…
                        </p>
                      )}
                      {secItems.map((it) => (
                        <ItemRow
                          key={it.id}
                          item={it}
                          sectionDisabled={secDisabled}
                          // CS/New overlays are meaningful only for sidebar
                          // items. Header widgets, Profile menu rows, and
                          // Account & Access page cards have their own
                          // shape (icons, tabs, cards) that would conflict
                          // with an extra chip — the toggles are hidden
                          // there to avoid confusing admins.
                          showOverlayToggles={!sec.location || sec.location === "sidebar"}
                          renaming={renamingId === it.id}
                          renameValue={renameValue}
                          setRenameValue={setRenameValue}
                          startRename={() => startRename(it.id, it.label)}
                          commitRename={commitRename}
                          cancelRename={cancelRename}
                          onToggle={() => toggleItemEnabled(it.id)}
                          onToggleComingSoon={() => toggleItemComingSoon(it.id)}
                          onToggleNew={() => toggleItemNew(it.id)}
                          onToggleViewport={(vp) => toggleItemViewport(it.id, vp)}
                          onPlansChange={(plans) => setItemPlans(it.id, plans)}
                          plansCatalog={plansCatalog}
                          onDragStart={(e) => onItemDragStart(e, it.id)}
                          onDragOver={(e) => onItemDragOver(e, it.id)}
                          onDrop={(e) => onItemDrop(e, it.id)}
                          onDragEnd={clearDrag}
                          dropHint={dropHint?.itemId === it.id ? dropHint.position : null}
                          // Children (for nav-modules / nav-tools / nav-databases)
                          childrenSource={childrenSource[it.id] || []}
                          childrenOverrides={childrenOverrides[it.id] || {}}
                          isDropdownParent={!!childrenSource[it.id]}
                          expanded={expandedDropdowns.has(it.id)}
                          onExpandToggle={() => toggleDropdown(it.id)}
                          renamingChild={renamingId}
                          startRenameChild={(childId, current) => startRename(`child:${it.id}:${childId}`, current)}
                          patchChild={(cid, patch) => patchChild(it.id, cid, patch)}
                          reorderChild={(sid, tid, before) => reorderChild(it.id, sid, tid, before)}
                        />
                      ))}
                    </div>
                  </div>
                  {showAfterHint && <DropDivider />}
                </React.Fragment>
              );
            })}

            <button
              onClick={addSection}
              data-testid="menu-directory-add-section"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-shell-border py-3 text-sm font-semibold text-muted-foreground hover:text-primary hover:border-primary/50 transition"
            >
              <Plus className="w-4 h-4" /> Add section
            </button>
          </div>
          </>
        )}
      </div>

      {deleteTarget && (
        <DeleteSectionModal
          section={deleteTarget}
          items={items.filter((it) => it.section === deleteTarget.id)}
          sections={sections}
          onClose={() => setDeleteTarget(null)}
          onConfirm={performDelete}
        />
      )}
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ItemRow({
  item, sectionDisabled, showOverlayToggles = true, renaming, renameValue, setRenameValue,
  startRename, commitRename, cancelRename, onToggle, onToggleComingSoon, onToggleNew,
  onToggleViewport, onPlansChange,
  plansCatalog,
  onDragStart, onDragOver, onDrop, onDragEnd, dropHint,
  // Children (only meaningful for dropdown items)
  childrenSource, childrenOverrides, isDropdownParent, expanded, onExpandToggle,
  renamingChild, startRenameChild, patchChild, reorderChild,
}) {
  const off = sectionDisabled || !item.enabled;
  return (
    <>
      {dropHint === "before" && <ItemDropDivider />}
      <div
        className={["group flex items-center gap-2 py-1.5 pl-2 pr-1 rounded-md",
          "hover:bg-muted/50 transition-colors cursor-move select-none"].join(" ")}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        data-testid={`menu-directory-item-${item.id}`}
      >
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
        {isDropdownParent && (
          <button
            onClick={onExpandToggle}
            className="p-0.5 rounded hover:bg-muted text-muted-foreground shrink-0"
            title={expanded ? "Collapse children" : "Show children"}
            data-testid={`menu-directory-expand-${item.id}`}
          >
            <ChevronDown className={["w-3 h-3 transition-transform",
              expanded ? "" : "-rotate-90"].join(" ")} />
          </button>
        )}
        {renaming ? (
          <RenameField
            value={renameValue}
            onChange={setRenameValue}
            onCommit={commitRename}
            onCancel={cancelRename}
            testid={`menu-directory-rename-input-${item.id}`}
            small
          />
        ) : (
          <button
            onClick={startRename}
            className={["text-sm flex-1 text-left truncate hover:text-primary transition",
              off ? "text-muted-foreground line-through" : ""].join(" ")}
            data-testid={`menu-directory-item-label-${item.id}`}
          >
            {item.label}
          </button>
        )}
        <span className="text-[9.5px] uppercase tracking-wider text-muted-foreground/60 shrink-0">
          {isDropdownParent && childrenSource
            ? `${childrenSource.length} pages`
            : (item.kind !== "simple" ? item.kind : "")}
        </span>
        <button
          onClick={startRename}
          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted text-muted-foreground transition-opacity"
          title="Rename item"
          data-testid={`menu-directory-rename-item-${item.id}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
        {!item.protected && (
          <PlansSelector
            id={item.id}
            selected={item.plans || []}
            catalog={plansCatalog}
            onChange={onPlansChange}
            compact
          />
        )}
        {item.protected ? (
          <span
            className="inline-flex items-center gap-1 text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0"
            title="This item is required — you can rename or move it but not disable it."
          >
            <Lock className="w-3 h-3" /> Required
          </span>
        ) : (
          <>
            {showOverlayToggles && (
              <>
                <MiniToggle
                  testid={`menu-directory-comingsoon-${item.id}`}
                  label="Coming Soon"
                  on={!!item.coming_soon}
                  onToggle={onToggleComingSoon}
                  disabled={sectionDisabled || !item.enabled}
                  title={
                    sectionDisabled || !item.enabled
                      ? "Enable the item first"
                      : (item.coming_soon ? "Click to unmark as coming soon" : "Freeze this item and show a Coming Soon chip")
                  }
                />
                <MiniToggle
                  testid={`menu-directory-new-${item.id}`}
                  label="New"
                  on={!!item.is_new}
                  onToggle={onToggleNew}
                  disabled={sectionDisabled || !item.enabled}
                  title={
                    sectionDisabled || !item.enabled
                      ? "Enable the item first"
                      : (item.is_new ? "Click to hide the New chip" : "Show the New chip next to this item")
                  }
                />
                <ViewportTogglesGroup
                  item={item}
                  onToggle={onToggleViewport}
                  disabled={sectionDisabled || !item.enabled}
                />
              </>
            )}
            <Toggle id={item.id} on={item.enabled} onToggle={onToggle} disabled={sectionDisabled} small />
          </>
        )}
      </div>
      {isDropdownParent && expanded && (
        <ChildrenPanel
          parentId={item.id}
          source={childrenSource}
          overrides={childrenOverrides}
          plansCatalog={plansCatalog}
          renamingId={renamingChild}
          renameValue={renameValue}
          setRenameValue={setRenameValue}
          startRenameChild={startRenameChild}
          commitRename={commitRename}
          cancelRename={cancelRename}
          patchChild={patchChild}
          reorderChild={reorderChild}
        />
      )}
      {dropHint === "after" && <ItemDropDivider />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Children (dropdown pages) — reorderable, renamable, hideable, plan-gateable
// ---------------------------------------------------------------------------
function ChildrenPanel({
  parentId, source, overrides, plansCatalog, renamingId, renameValue, setRenameValue,
  startRenameChild, commitRename, cancelRename, patchChild, reorderChild,
}) {
  const merged = useMemo(() => mergedChildren(parentId, {[parentId]: source}, {[parentId]: overrides}),
    [parentId, source, overrides]);
  const dragRef = useRef(null);
  const [hint, setHint] = useState(null); // {id, position}

  const onDragStart = (e, cid) => {
    dragRef.current = cid;
    e.dataTransfer.setData("text/plain", cid);
    e.dataTransfer.effectAllowed = "move";
    e.stopPropagation();
  };
  const onDragOver = (e, cid) => {
    if (!dragRef.current || dragRef.current === cid) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - rect.top) < rect.height / 2;
    setHint({ id: cid, position: before ? "before" : "after" });
  };
  const onDrop = (e, cid) => {
    if (!dragRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const src = dragRef.current;
    if (src !== cid) {
      const rect = e.currentTarget.getBoundingClientRect();
      const before = (e.clientY - rect.top) < rect.height / 2;
      reorderChild(src, cid, before);
    }
    dragRef.current = null;
    setHint(null);
  };

  return (
    <div
      className="ml-6 pl-3 border-l-2 border-primary/30 my-1 py-1"
      data-testid={`menu-directory-children-${parentId}`}
    >
      {merged.length === 0 && (
        <p className="text-xs italic text-muted-foreground px-2 py-1">No pages found.</p>
      )}
      {merged.map((child) => {
        const renameKey = `child:${parentId}:${child.id}`;
        const isRenaming = renamingId === renameKey;
        const disabled = !!child.disabled;
        const showBefore = hint?.id === child.id && hint.position === "before";
        const showAfter = hint?.id === child.id && hint.position === "after";
        return (
          <React.Fragment key={child.id}>
            {showBefore && <ItemDropDivider />}
            <div
              className={["group flex items-center gap-2 py-1 pl-2 pr-1 rounded-md",
                "hover:bg-muted/50 transition-colors cursor-move select-none"].join(" ")}
              draggable
              onDragStart={(e) => onDragStart(e, child.id)}
              onDragOver={(e) => onDragOver(e, child.id)}
              onDrop={(e) => onDrop(e, child.id)}
              onDragEnd={() => { dragRef.current = null; setHint(null); }}
              data-testid={`menu-directory-child-${parentId}-${child.id}`}
            >
              <GripVertical className="w-3 h-3 text-muted-foreground/60 shrink-0" />
              {isRenaming ? (
                <RenameField
                  value={renameValue}
                  onChange={setRenameValue}
                  onCommit={commitRename}
                  onCancel={cancelRename}
                  testid={`menu-directory-rename-input-${renameKey}`}
                  small
                />
              ) : (
                <button
                  onClick={() => startRenameChild(child.id, child.label)}
                  className={["text-[13px] flex-1 text-left truncate hover:text-primary transition",
                    disabled ? "text-muted-foreground line-through" : ""].join(" ")}
                  data-testid={`menu-directory-child-label-${parentId}-${child.id}`}
                >
                  {child.label}
                </button>
              )}
              <button
                onClick={() => startRenameChild(child.id, child.label)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted text-muted-foreground transition-opacity"
                title="Rename"
              >
                <Pencil className="w-2.5 h-2.5" />
              </button>
              <PlansSelector
                id={`${parentId}-${child.id}`}
                selected={child.plans || []}
                catalog={plansCatalog}
                onChange={(plans) => patchChild(child.id, { plans })}
                compact
              />
              <MiniToggle
                testid={`menu-directory-child-comingsoon-${parentId}-${child.id}`}
                label="Coming Soon"
                on={!!child.coming_soon}
                onToggle={() => patchChild(child.id, { coming_soon: !child.coming_soon })}
                disabled={disabled}
                title={
                  disabled
                    ? "Enable the item first"
                    : (child.coming_soon
                        ? "Click to unmark as coming soon"
                        : "Freeze this item and show a Coming Soon chip")
                }
              />
              <Toggle
                id={`${parentId}-${child.id}`}
                on={!disabled}
                onToggle={() => patchChild(child.id, { disabled: !disabled })}
                small
              />
            </div>
            {showAfter && <ItemDropDivider />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Merge the authoritative source list with local overrides. Overrides supply
// label/order/plans/disabled; missing fields fall back to source defaults.
function mergedChildren(parentId, sourceMap, overridesMap) {
  const source = sourceMap[parentId] || [];
  const overrides = overridesMap[parentId] || {};
  const withMeta = source.map((c, idx) => {
    const ov = overrides[c.id] || {};
    return {
      id: c.id,
      label: ov.label || c.label,
      plans: ov.plans || [],
      disabled: !!ov.disabled,
      coming_soon: !!ov.coming_soon,
      order: typeof ov.order === "number" ? ov.order : idx,
    };
  });
  withMeta.sort((a, b) => a.order - b.order);
  return withMeta;
}

function RenameField({ value, onChange, onCommit, onCancel, testid, small = false }) {
  return (
    <div className="flex items-center gap-1 flex-1 min-w-0">
      <input
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit();
          else if (e.key === "Escape") onCancel();
        }}
        onBlur={onCommit}
        data-testid={testid}
        className={[
          "flex-1 min-w-0 rounded border border-primary/50 bg-white px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/30",
          small ? "text-sm" : "text-base",
        ].join(" ")}
      />
      <button
        onMouseDown={(e) => { e.preventDefault(); onCommit(); }}
        className="p-1 rounded text-emerald-600 hover:bg-emerald-50"
        title="Save (Enter)"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        onMouseDown={(e) => { e.preventDefault(); onCancel(); }}
        className="p-1 rounded text-rose-500 hover:bg-rose-50"
        title="Cancel (Esc)"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Toggle({ id, on, onToggle, disabled = false, small = false }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      data-testid={`menu-directory-toggle-${id}`}
      role="switch"
      aria-checked={on}
      aria-label={on ? "Hide" : "Show"}
      title={disabled ? "Parent section is hidden" : (on ? "Click to hide" : "Click to show")}
      className={[
        "relative shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30",
        small ? "h-5 w-9" : "h-6 w-11",
        on ? "bg-primary" : "bg-muted",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
    >
      <span className={[
        "absolute top-0.5 rounded-full bg-white shadow transition-transform",
        small ? "h-4 w-4" : "h-5 w-5",
        on ? (small ? "translate-x-4" : "translate-x-5") : "translate-x-0.5",
      ].join(" ")}>
        {on ? <Eye className={[small ? "w-2.5 h-2.5" : "w-3 h-3", "m-0.5 text-primary"].join(" ")} />
            : <EyeOff className={[small ? "w-2.5 h-2.5" : "w-3 h-3", "m-0.5 text-muted-foreground"].join(" ")} />}
      </span>
    </button>
  );
}

// Compact label + toggle used for per-item overlays (Coming Soon, New).
// Kept intentionally short to fit alongside the primary visibility Toggle
// on a single row without wrapping — the label sits directly to the
// left of a tiny switch and disappears on very narrow admin viewports.
function MiniToggle({ testid, label, on, onToggle, disabled = false, title }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      data-testid={testid}
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={title}
      className={[
        "shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider transition-colors",
        on
          ? "border-primary/60 bg-primary/10 text-primary"
          : "border-shell-border bg-white text-muted-foreground hover:text-foreground",
        disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
      ].join(" ")}
    >
      <span className={[
        "inline-block h-1.5 w-1.5 rounded-full",
        on ? "bg-primary" : "bg-muted-foreground/40",
      ].join(" ")} />
      {label}
    </button>
  );
}

function DropDivider() {
  return <div className="h-1 rounded-full bg-primary/70 my-1" data-testid="menu-directory-drop-divider" />;
}


/**
 * Trio of viewport toggles (mobile / tablet / desktop) shown on every
 * enabled sidebar item. Turning one off hides the sidebar entry (chip
 * shown to members on that viewport) AND blocks direct-URL access
 * to the underlying page from that viewport via the route guard in
 * AppShell — so no share-link, DevTools resize, or physical rotate
 * lets a member sneak past the restriction. Configured entirely from
 * Menu Directory — no code changes required to restrict a link to
 * bigger screens.
 */
function ViewportVisibilityNote() {
  return (
    <div
      className="surface flex items-start gap-3 p-4 mb-4 bg-muted/40"
      data-testid="menu-directory-viewport-note"
    >
      <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
      <div className="text-xs text-muted-foreground leading-relaxed">
        <p className="font-semibold text-foreground text-[13px] mb-1 inline-flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5" />
          <Tablet className="w-3.5 h-3.5" />
          <Monitor className="w-3.5 h-3.5" />
          Viewport visibility
        </p>
        <p>
          Each item has a mobile / tablet / desktop switch. Turning one <span className="font-semibold text-foreground">off</span> hides that item from the sidebar on that viewport <span className="font-semibold text-foreground">and</span> blocks the page from loading there — even by direct URL, a shared link, or DevTools viewport switching. Members on a blocked viewport see a "View on desktop" message and can't reach the content until they use an allowed device.
        </p>
      </div>
    </div>
  );
}
function ViewportTogglesGroup({ item, onToggle, disabled }) {
  const vp = item.viewports || { mobile: true, tablet: true, desktop: true };
  const entries = [
    { key: "mobile",  Icon: Smartphone, label: "Show on mobile" },
    { key: "tablet",  Icon: Tablet,     label: "Show on tablet" },
    { key: "desktop", Icon: Monitor,    label: "Show on desktop" },
  ];
  return (
    <div
      className="shrink-0 inline-flex items-center rounded-full border border-shell-border bg-white/70 divide-x divide-shell-border"
      data-testid={`menu-directory-viewports-${item.id}`}
    >
      {entries.map(({ key, Icon, label }) => {
        const on = !!vp[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            disabled={disabled}
            role="switch"
            aria-checked={on}
            aria-label={label}
            title={
              disabled
                ? "Enable the item first"
                : `${on ? "Hide" : "Show"} on ${key} — currently ${on ? "clickable" : "blocked (chip in sidebar + direct URL is guarded)"}`
            }
            data-testid={`menu-directory-viewport-${key}-${item.id}`}
            className={[
              "px-1.5 py-0.5 inline-flex items-center gap-1 text-[10px] transition-colors first:rounded-l-full last:rounded-r-full",
              on
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground",
              disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            <Icon className="w-3 h-3" />
          </button>
        );
      })}
    </div>
  );
}
function ItemDropDivider() {
  return <div className="h-0.5 rounded-full bg-primary/70 mx-2" data-testid="menu-directory-item-drop-divider" />;
}

function DeleteSectionModal({ section, items, sections, onClose, onConfirm }) {
  const [typed, setTyped] = useState("");
  const [moveTo, setMoveTo] = useState(() => {
    const first = sections.find((s) => s.id !== section.id);
    return first?.id || "";
  });
  const canConfirm = typed === "DELETE" && (items.length === 0 || !!moveTo);
  const otherSections = sections.filter((s) => s.id !== section.id);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="menu-directory-delete-modal"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-rose-100 text-rose-600">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-xl">Delete "{section.label}"?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This can't be undone from the sidebar (use <em>Reset to defaults</em> to restore baseline sections).
            </p>
          </div>
        </div>

        {items.length > 0 && (
          <div className="mt-5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Move {items.length} item{items.length === 1 ? "" : "s"} to
            </label>
            <select
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              data-testid="menu-directory-delete-move-to"
              className="w-full rounded border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary bg-white"
            >
              {otherSections.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <ul className="mt-2 text-xs text-muted-foreground list-disc list-inside max-h-24 overflow-auto">
              {items.map((it) => <li key={it.id}>{it.label}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-5">
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Type <span className="text-rose-600 font-mono">DELETE</span> to confirm
          </label>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid="menu-directory-delete-typed"
            placeholder="DELETE"
            className="w-full rounded border border-shell-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-rose-500"
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="menu-directory-delete-cancel"
            className="rounded-full border border-shell-border px-4 py-1.5 text-xs hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(section.id, moveTo)}
            disabled={!canConfirm}
            data-testid="menu-directory-delete-confirm"
            className={["inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-semibold transition",
              canConfirm ? "bg-rose-600 text-white hover:bg-rose-700"
                         : "bg-muted text-muted-foreground cursor-not-allowed"].join(" ")}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete section
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function randHex(n) {
  const chars = "abcdef0123456789";
  let out = "";
  for (let i = 0; i < n; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function serialize(sections, items, childrenOverrides) {
  // Stable representation for dirty-checking. We compare arrays after sorting.
  return {
    s: sections.map((s) => ({ id: s.id, label: s.label, enabled: s.enabled, plans: [...(s.plans || [])].sort() })),
    i: items.map((it) => ({ id: it.id, section: it.section, label: it.label, order: it.order,
                             enabled: it.enabled,
                             coming_soon: !!it.coming_soon,
                             is_new: !!it.is_new,
                             viewports: it.viewports || { mobile: true, tablet: true, desktop: true },
                             plans: [...(it.plans || [])].sort() })),
    c: childrenOverrides || {},
  };
}

// ---------------------------------------------------------------------------
// Plans selector — shows a pill with the plan restriction summary; clicking
// opens a small popover with checkboxes for every canonical plan.
// ---------------------------------------------------------------------------
function PlansSelector({ id, selected, catalog, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (slug) => {
    const next = selected.includes(slug)
      ? selected.filter((s) => s !== slug)
      : [...selected, slug];
    onChange(next);
  };
  const clearAll = () => onChange([]);

  const allPlans = selected.length === 0;
  const summary = allPlans
    ? "All plans"
    : selected.map((s) => (catalog.find((c) => c.slug === s)?.label || s)).join(", ");

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`menu-directory-plans-${id}`}
        title={`Visible on: ${summary}`}
        className={[
          "inline-flex items-center gap-1 rounded-full border text-[10px] font-semibold uppercase tracking-wider transition",
          compact ? "px-2 py-0.5" : "px-2.5 py-1",
          allPlans
            ? "border-shell-border text-muted-foreground hover:bg-muted"
            : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20",
        ].join(" ")}
      >
        <Users2 className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
        <span className="max-w-[7rem] truncate">{summary}</span>
        <ChevronDown className={compact ? "w-2.5 h-2.5" : "w-3 h-3"} />
      </button>
      {open && (
        <div
          className="absolute z-40 right-0 top-full mt-1 min-w-[10rem] rounded-lg border border-shell-border bg-white shadow-lg p-2"
          data-testid={`menu-directory-plans-popover-${id}`}
        >
          <p className="px-1.5 py-1 text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground">
            Visible on
          </p>
          {catalog.map((p) => {
            const on = selected.includes(p.slug);
            return (
              <label
                key={p.slug}
                className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-muted cursor-pointer text-sm"
                data-testid={`menu-directory-plan-option-${id}-${p.slug}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(p.slug)}
                  className="accent-primary"
                />
                <span className="flex-1">{p.label}</span>
              </label>
            );
          })}
          <div className="border-t border-shell-border mt-1 pt-1">
            <button
              onClick={clearAll}
              className="w-full text-left px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted rounded"
              data-testid={`menu-directory-plans-clear-${id}`}
            >
              All plans (no restriction)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Pages tab ────────────────────────────────────────────────────────
// Renders the static registry the backend ships (via
// `PAGE_ELEMENTS_REGISTRY`) with a Visibility On/Off switch per element.
// Elements hang beneath the page name using the same indented
// left-rail treatment as the dropdown children on the Member Sidebar
// tab (see `ChildList` above) so the two admin surfaces feel
// consistent. Toggling flips the id in the parent's
// `pageElementsDisabled` set — nothing round-trips until Save.
function PagesPanel({ pages, disabled, onToggle }) {
  if (!pages || pages.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="pages-empty">
        No page-level toggles are registered yet. Add entries to
        <code className="mx-1 rounded bg-shell-muted px-1.5 py-0.5">PAGE_ELEMENTS_REGISTRY</code>
        in <code className="rounded bg-shell-muted px-1.5 py-0.5">backend/routers/menu_directory.py</code>.
      </p>
    );
  }
  return (
    <div className="grid gap-3" data-testid="pages-panel">
      {pages.map((page) => {
        const elements = page.elements || [];
        return (
          <div
            key={page.page_id}
            className="surface p-4"
            data-testid={`page-card-${page.page_id}`}
          >
            {/* Page header — matches the dropdown-parent row look. */}
            <div className="flex items-center justify-between gap-3 py-1">
              <div className="min-w-0">
                <p className="font-display text-base leading-tight">{page.label}</p>
                {page.route && (
                  <p className="mt-0.5 text-[11px] font-mono text-muted-foreground">
                    {page.route}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                {elements.length} element{elements.length === 1 ? "" : "s"}
              </span>
            </div>

            {/* Elements — hang below the page name with the same
                indented left-rail that dropdown children use in the
                Member Sidebar tab. */}
            <div className="ml-6 pl-3 border-l-2 border-primary/30 my-1 py-1 grid gap-1">
              {elements.length === 0 && (
                <p className="text-xs italic text-muted-foreground px-2 py-1">
                  No toggleable elements on this page yet.
                </p>
              )}
              {elements.map((el) => {
                const isHidden = disabled.has(el.id);
                return (
                  <div
                    key={el.id}
                    data-testid={`page-element-row-${el.id}`}
                    className="group flex items-center justify-between gap-3 py-1 pl-2 pr-1 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-shell-foreground leading-tight">
                        {el.label}
                      </p>
                      {el.description && (
                        <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
                          {el.description}
                        </p>
                      )}
                      <p className="mt-0.5 text-[10px] font-mono text-muted-foreground/70">
                        {el.id}
                      </p>
                    </div>
                    <button
                      onClick={() => onToggle(el.id)}
                      data-testid={`page-element-toggle-${el.id}`}
                      className={[
                        "shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest inline-flex items-center gap-1.5 transition",
                        isHidden
                          ? "bg-shell-muted text-muted-foreground hover:bg-shell-muted/80"
                          : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200",
                      ].join(" ")}
                      title={isHidden ? "Element hidden — click to show" : "Element visible — click to hide"}
                    >
                      {isHidden ? (
                        <><EyeOff className="w-3.5 h-3.5" /> Hidden</>
                      ) : (
                        <><Eye className="w-3.5 h-3.5" /> Visible</>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
